import { Button, Card, CardBody, Flex, Heading, Image, Square, Text } from "@chakra-ui/react";
import { FormPage as SendNFTForm } from "@components/SendFlow/NFT/FormPage";
import { useColor } from "@styles/useColor";
import {
  ChainFullIds,
  type UnifiedNFTBalance,
  artifactUri,
  getIPFSurl,
  isTezosNft,
  mimeType,
  tokenName,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { useGetNetworkName } from "@umami/state";
import { isMultichainEnabled } from "@umami/utils";
import ReactPlayer from "react-player";

import { AttributesAccordion } from "./AttributesAccordion";
import { JSONAccordion } from "./JSONAccordion";
import { PropertiesAccordion } from "./PropertiesAccordion";
import { TagsSection } from "./TagsSection";
import { NFTBalancePill } from "../NFTBalancePill";

import { Tag } from "@/components/Tag/Tag";
import { useCorsImageUrl } from "@/hooks/useCorsImageUrl";

const getNFTData = (nft: UnifiedNFTBalance) => {
  if (isTezosNft(nft)) {
    const url = getIPFSurl(artifactUri(nft));
    const fallbackUrl = getIPFSurl(nft.displayUri);
    const isVideo = mimeType(nft)?.startsWith("video/");
    const name = tokenName(nft);

    return { url, fallbackUrl, name, isVideo };
  }

  return {
    url: nft.animationUrl || nft.imageUrl,
    name: nft.name,
    fallbackUrl: nft.metadata.image,
    isVideo: !!nft.animationUrl,
  };
};

export const NFTDrawerCard = ({ nft }: { nft: UnifiedNFTBalance }) => {
  const color = useColor();
  const { openWith } = useDynamicModalContext();
  const { url, fallbackUrl, name, isVideo } = getNFTData(nft);
  const networkName = useGetNetworkName()(nft.chainId);
  const corsImageUrl = useCorsImageUrl(url);
  const corsFallbackImageUrl = useCorsImageUrl(fallbackUrl);

  const chainId = isTezosNft(nft) ? ChainFullIds.TEZOS : nft.chainId;

  return (
    <Flex flexDirection="column">
      <Card boxShadow="none">
        <CardBody
          justifyContent="center"
          display="flex"
          maxHeight={{ base: "366px", md: "446px" }}
          padding="0"
          aspectRatio={1}
        >
          <Square
            maxHeight={{ base: "366px", md: "446px" }}
            background={color("50")}
            borderRadius="6px"
            size="full"
          >
            {isVideo ? (
              <ReactPlayer
                controls
                data-testid="nft-video"
                loop
                playing
                playsinline
                url={corsImageUrl}
              />
            ) : (
              <Image
                width="full"
                maxHeight={{ base: "366px", md: "446px" }}
                borderRadius="6px"
                objectFit="contain"
                data-testid="nft-image"
                fallbackSrc={corsFallbackImageUrl}
                src={corsImageUrl}
              />
            )}

            <NFTBalancePill nft={nft} />
          </Square>
        </CardBody>
      </Card>

      {isTezosNft(nft) && <TagsSection nft={nft} />}

      <Heading color={color("900")} marginY="12px" size="xl">
        {name}
      </Heading>

      {isMultichainEnabled() && (
        <Tag marginBottom="12px" data-testid="chain-tag" size="sm" variant="square">
          {networkName}
        </Tag>
      )}

      <Text color={color("600")} size="sm">
        {nft.metadata.description}
      </Text>

      <Button
        width="fit-content"
        marginTop="20px"
        padding="10px 24px"
        onClick={() => openWith(<SendNFTForm chainId={chainId} nft={nft} />)}
        size="lg"
        variant="primary"
      >
        Send
      </Button>

      <AttributesAccordion marginTop="20px" nft={nft} />

      {isTezosNft(nft) && (
        <PropertiesAccordion marginTop={{ base: "24px", md: "30px" }} nft={nft} />
      )}

      <JSONAccordion marginTop={{ base: "24px", md: "30px" }} nft={nft} />
    </Flex>
  );
};
